import { useContext, useCallback } from 'react';
import { UserContext } from '../../context/UserContext';
import { getCommunityLoginUrl } from '@atlassian/community-library';
import { getEnvironmentAsEnum } from '../../utils/environment';
import SectionButton from '../common/SectionButton';
import { DEFAULT_CHAMPIONS_SIGNUP_URL } from '../../utils/constants';
import { championsAuthSignupIsActive } from '../../services/statsig/featureGates';
import { getButtonEventPayload } from '../../analytics/util';
import { ANALYTICS_ACTIONS, ANALYTICS_DEFAULTS } from '../../analytics/constants';

// Internal route for champions apply page
const CHAMPIONS_APPLY_PATH = '/champions/apply';

interface AuthGatedApplyButtonProps {
  /** Unique identifier for analytics - e.g., 'championsHeroButton' or 'championsAwardsButton' */
  buttonId: string;
  /** Section identifier for analytics - e.g., 'championsHeroSection' or 'championsAwardsSection' */
  sectionId: string;
}

/**
 * Apply Now Button with Feature-Flagged Authentication
 * 
 * This component renders the primary CTA for Champions program applications,
 * with intelligent routing based on authentication state and feature gates.
 * 
 * Analytics Strategy:
 * - Tracks button clicks and hovers with full context (auth state, feature gate, entry point)
 * - Entry point tracking enables funnel analysis to optimize conversion paths
 * - User ID tracking allows for user-level journey analysis
 * - Feature gate tracking enables A/B testing analysis between old and new flows
 * 
 * Flow Logic:
 * 
 * When championsAuthSignupIsActive feature gate is ENABLED:
 * - If user is logged in: redirects to internal champions signup page with entry point tracking
 * - If user is not logged in: redirects to login page, then to champions signup page after authentication
 * 
 * When championsAuthSignupIsActive feature gate is DISABLED:
 * - Redirects to external Salesforce signup page (legacy flow)
 * 
 * @param buttonId - Unique identifier for analytics tracking (e.g., 'championsHeroButton')
 * @param sectionId - Section identifier used for entry point tracking (e.g., 'championsHeroSection')
 */
export const AuthGatedApplyButton = ({ buttonId, sectionId }: AuthGatedApplyButtonProps) => {
  const userData = useContext(UserContext);
  const user = userData?.user;
  const isLoggedIn = !!user;
  const userId = user?.account_id;
  const currentEnv = getEnvironmentAsEnum();
  const isAuthSignupEnabled = championsAuthSignupIsActive();

  // Internal signup path with entryPoint tracking
  const applyPathWithEntryPoint = `${CHAMPIONS_APPLY_PATH}?entryPoint=${sectionId}`;
  
  // Full URL for the apply page (used for login redirect)
  const applyFullUrl = `${process.env.HOME_BASE_URL}${applyPathWithEntryPoint}`;

  // Determine the href based on feature flag and login status
  const getHref = useCallback(() => {
    // If feature flag is disabled, use the old external URL flow
    if (!isAuthSignupEnabled) {
      return DEFAULT_CHAMPIONS_SIGNUP_URL;
    }

    // Feature flag is enabled - use auth-gated flow
    if (isLoggedIn) {
      // User is logged in, redirect to internal apply page
      return applyPathWithEntryPoint;
    } else {
      // User is not logged in, redirect to login with apply page as the post-login destination
      return getCommunityLoginUrl(
        {
          redirectUrl: applyFullUrl,
          experience: 'champion-apply',
        },
        currentEnv,
      );
    }
  }, [isAuthSignupEnabled, isLoggedIn, currentEnv, applyFullUrl, applyPathWithEntryPoint]);

  const href = getHref();

  // Use _blank for external URL, _self for internal navigation
  const target = isAuthSignupEnabled ? '_self' : '_blank';

  // Extended attributes for enhanced analytics tracking
  const extendedAttributes = {
    isAuthenticated: isLoggedIn,
    userId: userId || ANALYTICS_DEFAULTS.ANONYMOUS_USER,
    featureGateEnabled: isAuthSignupEnabled,
    entryPoint: sectionId,
  };

  return (
    <SectionButton
      href={href}
      text="Apply now"
      target={target}
      hasArrow={false}
      clickEventPayload={getButtonEventPayload(
        ANALYTICS_ACTIONS.CLICKED,
        buttonId,
        sectionId,
        href,
        'Apply now',
        extendedAttributes,
      )}
      hoverEventPayload={getButtonEventPayload(
        ANALYTICS_ACTIONS.HOVERED,
        buttonId,
        sectionId,
        href,
        'Apply now',
        extendedAttributes,
      )}
    />
  );
};
